import { Database, FileCheck2, KeyRound, LockKeyhole, ScrollText, ShieldCheck, UserCheck } from "lucide-react";
import { PublicFooter, PublicHeader } from "@/components/marketing/PublicHeader";
import { useSeo } from "@/components/marketing/MarketingPrimitives";
import ScrollToTopButton from "@/components/marketing/ScrollToTopButton";

export default function HanaSecurityPage() {
  const pillars = [
    [ShieldCheck, "Accès par rôle", "Chacun voit uniquement ce qui relève de son rôle : patient, infirmière de coordination, médecin, administrateur, partenaire."],
    [ScrollText, "Consentement", "Recueilli, daté, révocable. La participation à la recherche est un choix distinct du soin et ne conditionne jamais le suivi."],
    [LockKeyhole, "Données structurées", "Les informations de suivi sont classées pour le soin. Chaque action clé est tracée : qui, quoi, quand."],
    [KeyRound, "Pseudonymisation forte", "Pour la recherche, les identités sont remplacées par des identifiants pseudonymisés et seuls des champs contrôlés sont exportés, avec techniques d’anonymisation."],
    [Database, "Données pour la recherche", "Les partenaires reçoivent des indicateurs agrégés, jamais de dossier individuel. Les petits effectifs sont protégés."],
    [UserCheck, "Décision médicale", "Hana trie et présente les informations. La décision médicale reste celle de l’équipe soignante."],
    [FileCheck2, "Cadre contractuel", "Contrats, liste des livrables et règles de gouvernance sont prêts pour chaque partenaire."],
  ];

  useSeo("Sécurité et données — HANA", "Sécurité, accès par rôle, consentement, traçabilité, pseudonymisation forte avec techniques d’anonymisation et conformité CNDP pour l’infrastructure de coordination HANA.");
  return (
    <main className="min-h-screen bg-white text-slate-800" data-testid="security-page">
      <PublicHeader current="/securite" />
      <section className="bg-slate-900 px-5 py-20 text-white lg:px-8 lg:py-28" data-testid="security-hero-section">
        <div className="mx-auto max-w-7xl">
          <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-teal-300">Sécurité et données</p>
          <h1 className="mt-5 max-w-4xl font-display text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl" data-testid="security-title">Des données de santé protégées, une responsabilité médicale claire.</h1>
          <p className="mt-6 max-w-3xl text-lg leading-8 text-slate-300" data-testid="security-description">HANA structure le suivi. La clinique garde la décision médicale. Les données du parcours ne sont jamais exposées inutilement, et aucun usage secondaire n’a lieu sans consentement.</p>
        </div>
      </section>
      <section className="px-5 py-20 lg:px-8" data-testid="security-pillars-section">
        <div className="mx-auto grid max-w-7xl gap-5 md:grid-cols-2 lg:grid-cols-3">
          {pillars.map(([Icon, title, text]) => (
            <article key={title} className="rounded-3xl border border-emerald-100 bg-white p-6 shadow-sm" data-testid={`security-pillar-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <Icon className="h-7 w-7 text-teal-500" />
              <h2 className="mt-5 text-xl font-extrabold text-slate-800">{title}</h2>
              <p className="mt-3 text-sm leading-6 text-slate-500">{text}</p>
            </article>
          ))}
        </div>
      </section>
      <section className="px-5 pb-20 lg:px-8" data-testid="security-architecture-section">
        <div className="mx-auto max-w-7xl rounded-[2rem] border border-emerald-100 bg-emerald-50 p-8 md:p-12">
          <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-emerald-700">Comment circule l’information</p>
          <div className="mt-8 grid gap-4 md:grid-cols-5">
            {["Le patient transmet", "HANA structure et priorise", "L’équipe décide", "Consentement distinct pour la recherche", "Indicateurs agrégés"].map((step, index) => (
              <div key={step} className="rounded-2xl bg-white p-5 text-center shadow-sm" data-testid={`security-flow-step-${index + 1}`}>
                <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-emerald-600 text-sm font-extrabold text-white">{index + 1}</span>
                <p className="mt-4 text-sm font-extrabold text-slate-800">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <PublicFooter />
      <ScrollToTopButton />
    </main>
  );
}